// components/version-update-trigger.tsx — mounts the version-update dialog
// and the "block on next user action" capture listener that surfaces it.
//
// Flow:
//   1. useAppVersion (parent) flips { outdated: true, detected: <sha> } when
//      /api/ returns a version that differs from the build-time
//      __APP_VERSION__ inlined by vite.config.ts.
//   2. We install a CAPTURE-phase listener on document for the first click
//      or keydown after that flip. preventDefault + stopPropagation means
//      whatever the user was about to do (click a nav link, submit a form)
//      does NOT happen — they get the modal instead. One-shot: the
//      listener is removed after the first event so dismissing the dialog
//      with "Later" lets the user keep working on the stale bundle for
//      the rest of this session without being nagged again.
//   3. <VersionUpdateDialog> does the actual UI. Reload calls
//      window.location.reload(); Later just closes.
//
// ponytail: useEffect's deps are intentionally just [outdated, detected].
// Resolving the listener inside the effect with a fresh closure means we
// don't need a ref to remember "have we installed the listener yet" — the
// effect re-runs on every state change, and the cleanup tears the previous
// listener down. With [outdated, detected] and an idempotent install/remove
// pair, StrictMode's double-invoke in dev still leaves exactly one
// listener mounted at a time.

import { useEffect, useState } from "react";

import { VersionUpdateDialog } from "@/components/version-update-dialog";
import { useAppVersion } from "@/lib/use-app-version";

export function reloadPage() {
  window.location.reload();
}

type VersionUpdateTriggerProps = {
  // ponytail: injected so browser tests can spy on the reload side-effect —
  // window.location.reload isn't mockable in Chromium, and the production
  // caller (__root.tsx) doesn't pass anything. Default uses reloadPage
  // directly so the prop is invisible at the call site.
  reloadFn?: () => void;
};

export function VersionUpdateTrigger({ reloadFn = reloadPage }: VersionUpdateTriggerProps = {}) {
  const { outdated, detected } = useAppVersion();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!outdated || detected === null) return;

    const handler = (e: MouseEvent | KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
    };
    const opts: AddEventListenerOptions = { capture: true, once: true };
    document.addEventListener("click", handler, opts);
    document.addEventListener("keydown", handler, opts);

    return () => {
      document.removeEventListener("click", handler, true);
      document.removeEventListener("keydown", handler, true);
    };
  }, [outdated, detected]);

  if (!outdated || detected === null) return null;

  return (
    <VersionUpdateDialog
      open={open}
      onOpenChange={setOpen}
      onReload={reloadFn}
      clientVersion={__APP_VERSION__}
      serverVersion={detected}
    />
  );
}
